import type { Fact, OcrResult, StructuredDocument } from '../../types/document'
import { classify } from './classify'
import {
  documentRequestIndexes,
  extractActions,
  extractAmounts,
  extractAuthority,
  extractContacts,
  extractDates,
  extractPerson,
  extractPlace,
  extractReferences,
  extractRequiredDocuments,
  extractStatus,
  extractWarnings,
  pickDeadline
} from './fields'
import { isGarbage, maskAadhaar, segment } from './text'
import { extractLabTests, looksLikeLabReport } from './lab'
import { AGREEMENT_TYPES, extractClauses } from './clauses'

export { formatRupees } from './fields'
export { segment, normalizeDigits, maskAadhaar } from './text'

function mask<T extends Fact<unknown>>(item: T): T {
  return {
    ...item,
    evidence: maskAadhaar(item.evidence),
    value: typeof item.value === 'string' ? maskAadhaar(item.value) : item.value
  }
}

/**
 * Deterministic document understanding. Runs entirely on the device; nothing
 * here calls a network service, and every field is copied from the text.
 */
export function extractDocument(ocr: OcrResult): StructuredDocument {
  const segments = segment(ocr.lines)
  const status = extractStatus(segments)
  // A letter that asks for an "income certificate" is not itself an income certificate
  const requests = documentRequestIndexes(segments)
  const typeSegments = segments.filter((_seg, index) => !requests.has(index))
  const classified = classify(typeSegments.length > 0 ? typeSegments : segments, status?.value ?? null)
  // Lab reports are recognised by their result rows (value, unit, printed range)
  const labTests = extractLabTests(ocr.lines)
  const isLab = looksLikeLabReport(labTests, ocr.text)
  const { type, confidence, evidence } = isLab
    ? { type: 'lab_report' as const, confidence: 'high' as const, evidence: labTests.slice(0, 3).map((t) => t.name) }
    : classified
  const dates = extractDates(segments)
  const meaningfulLines = ocr.lines.filter((l) => l.text.trim())

  const doc: StructuredDocument = {
    documentType: type,
    typeConfidence: confidence,
    typeEvidence: evidence,
    person: extractPerson(segments),
    status,
    dates,
    deadline: pickDeadline(dates),
    amounts: extractAmounts(segments),
    references: extractReferences(segments),
    authority: extractAuthority(segments),
    place: extractPlace(segments),
    contacts: extractContacts(segments),
    actions: extractActions(segments),
    documentsRequired: extractRequiredDocuments(segments),
    warnings: extractWarnings(segments),
    labTests: isLab ? labTests : [],
    clauses: (AGREEMENT_TYPES as readonly string[]).includes(type) ? extractClauses(segments) : [],
    quality: {
      ocrConfidence: Math.round(ocr.confidence),
      unclearLines: meaningfulLines.filter((l) => l.confidence < 60 || isGarbage(l.text)).length,
      totalLines: meaningfulLines.length,
      charCount: ocr.text.replace(/\s/g, '').length,
      pages: ocr.pages,
      source: ocr.source
    }
  }

  return {
    ...doc,
    person: doc.person && mask(doc.person),
    status: doc.status && mask(doc.status),
    dates: doc.dates.map(mask),
    deadline: doc.deadline && mask(doc.deadline),
    amounts: doc.amounts.map(mask),
    references: doc.references.map(mask),
    authority: doc.authority && mask(doc.authority),
    place: doc.place && mask(doc.place),
    contacts: doc.contacts.map(mask),
    actions: doc.actions.map(mask),
    documentsRequired: doc.documentsRequired.map(mask),
    warnings: doc.warnings.map(mask),
    labTests: doc.labTests?.map((t) => ({ ...t, evidence: maskAadhaar(t.evidence) })),
    clauses: doc.clauses?.map(mask)
  }
}

/** Is there enough readable text to say anything at all? */
export function hasReadableText(ocr: OcrResult): boolean {
  const readable = ocr.lines.filter((l) => !isGarbage(l.text) && l.confidence >= 40)
  return readable.map((l) => l.text).join('').replace(/\s/g, '').length >= 20
}
